/**
 *
 * SmallCalendar
 *
 */

import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import IconButton from 'components/interaction/IconButton/index.jsx';
import TimeGrid from 'components/TimeGrid/index.jsx';
import { format, SHORTDATE } from 'utils/date-fns/index.js';
import { useWidth } from 'utils/checkMedia.js';
import { useFixedBottomStyle } from 'utils/fixedBottomStyle.js';
import messages from '../messages.js';
import TimeSlot from './TimeSlot.jsx';
import StudentHeaders from '../StudentHeaders.jsx';
import { EVENT_HEIGHT } from './constants.js';
import getLockedMessage from '../getLockedMessage.js';

const Wrapper = styled.div`
  overflow: auto;
  & > div {
    min-width: ${(p) => (p.nCols * 120) + 45}px; // extra for left margin with time
  }
`;

const CalendarWrapper = styled.div`
  border: 1px solid ${(p) => p.theme.tableBorderColor};
  & > div {
    position: relative;
    height: ${(p) => p.height}px;
    margin-left: 47px;
    & > *:first-child {
      margin-left: -45px;
    }
  }
`;

function SmallCalendar(props) {
  const { meetingSet, events, block, students, onClose, student } = props;
  const { duration, inBetween } = meetingSet;
  const lockedMessage = useMemo(() => getLockedMessage(meetingSet, student.userId), [meetingSet, student]);
  const isSmallScreen = useWidth('sm');
  const [ref, bottomStyle] = useFixedBottomStyle(window.socsIsInAppWebView ? 232 : 520);

  const blockTime = duration + inBetween;
  const calendar = useRef();
  const sizes = useMemo(() => {
    const min = Math.min(...events.map((ev) => ev.start.getTime()));
    const max = Math.max(...events.map((ev) => ev.end.getTime()));
    const heightPerMinute = EVENT_HEIGHT / blockTime;
    const resolution = heightPerMinute / 60000;
    return { min, max, resolution }; // px per msec
  }, [events, blockTime]);

  function showBlock(blk) {
    const active = blk.set.id === meetingSet.id && blk.student.id === student.userId;
    return blk.timeslots
      .map((slot, idx) => (
        <TimeSlot
          key={slot.id}
          {...props}
          sizes={sizes}
          active={active}
          slot={slot}
          block={blk}
          selected={blk.id === block.id && idx === 0}
          lockedMessage={lockedMessage}
          duration={blockTime}
        />
      ));
  }

  const height = (sizes.max - sizes.min) * sizes.resolution;
  const dateFormat = isSmallScreen ? SHORTDATE : 'iiii d MMMM';
  const studentsWithEvent = students.map((stud, idx) => ({
    ...stud,
    event: events[idx].set.title,
  }));
  return (
    <>
      <div className="d-flex justify-content-between border-top border-bottom py-2 mb-2">
        <IconButton variant="link" icon="ss-navigateleft" onClick={onClose}>
          <FormattedMessage {...messages.backToOverview} />
        </IconButton>
        <h3>{format(block.start, dateFormat, true)}</h3>
      </div>
      <Wrapper nCols={studentsWithEvent.length}>
        <div>
          <StudentHeaders students={studentsWithEvent} className="sticky" />
          <CalendarWrapper height={height} ref={ref} style={bottomStyle}>
            <div ref={calendar}>
              <TimeGrid sizes={sizes} step={30} />
              {events.map(showBlock)}
            </div>
          </CalendarWrapper>
        </div>
      </Wrapper>
    </>
  );
}

SmallCalendar.propTypes = {
  student: PropTypes.object,
  students: PropTypes.array,
  onClose: PropTypes.func,
  meetingSet: PropTypes.object,
  block: PropTypes.object,
  events: PropTypes.array,
};

export default SmallCalendar;
